// variant-d.jsx — Hero-led Editorial layout (the default).
// Expanded in Phase 3a to cover the full desktop feature set:
// sun countdown, minutely precip, suggestions, hourly, 10-day, UV timeline,
// wind detail, metrics, almanac + historical, pollen, moon, field notes, quote.

import { useEffect, useState } from 'preact/hooks';
import { WxIcon, Glyph } from '../icons.jsx';
import { HeroMedia } from '../hero/hero-media.jsx';
import { PullToRefresh, DayRow, HourCell, MetricChip, AlertBanner, AuthFooter } from '../atoms.jsx';
import {
  getLocation, getUpdatedLabel, getCurrentForState,
  getHourly, getTenDay, getMetrics, getAlert, hasActiveAlert,
  getFieldNotes, getQuote, getNarrative,
  getSunCountdown, getMinutely, getSuggestions,
  getUvTimeline, getWindDetail, getAlmanac, getHistoricalDelta,
  getPollen, getMoonRow, getPressureTrend,
  getRawState, refresh,
} from '../data-bridge.js';
import { isSectionEnabled, subscribeSections } from '../section-prefs.js';
import { HourlySparklines } from '../charts/sparklines.jsx';
import { RadarMap } from '../maps/radar-map.jsx';
import { PoiNearby } from '../maps/poi-map.jsx';

// Reusable editorial section header — numbered eyebrow + horizontal divider + h2.
function Section({ brand, num, eyebrow, title }) {
  return (
    <div style={{ padding: `${20}px 16px 0` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontFamily: brand.eyebrowFont, color: brand.accent, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700 }}>{num}</span>
        <div style={{ flex: 1, height: 1, background: brand.line }}/>
        <span style={{ fontSize: 11, fontFamily: brand.eyebrowFont, color: brand.textDim, letterSpacing: '.06em', textTransform: 'uppercase' }}>{eyebrow}</span>
      </div>
      <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0, letterSpacing: '-.02em', color: brand.text }}>{title}</h2>
    </div>
  );
}

export function VariantD({ brand, density, state, sheetOpen, setSheetOpen, tipFn, heroTreatment = 'punch', bucket = 'midday', brandKey = 'shared' }) {
  const s = getCurrentForState(state);
  const hourly = getHourly(state);
  const tenDay = getTenDay(state);
  const metrics = getMetrics(state);
  const alert = getAlert();
  const fieldNotes = getFieldNotes();
  const quote = getQuote();
  const location = getLocation();
  const updated = getUpdatedLabel();
  const narrative = getNarrative(state);

  const sunCount = getSunCountdown();
  const minutely = getMinutely();
  const suggestions = getSuggestions();
  const uvTimeline = getUvTimeline();
  const windDetail = getWindDetail();
  const almanac = getAlmanac();
  const histDelta = getHistoricalDelta();
  const pollen = getPollen();
  const moonRow = getMoonRow(5);
  const pressureTrend = getPressureTrend();

  const max = Math.max(...tenDay.map((d) => d.hi));
  const min = Math.min(...tenDay.map((d) => d.lo));
  const [expanded, setExpanded] = useState(null);
  const showAlert = hasActiveAlert() || state === 'storm' || state === 'rain';

  // Re-render when section prefs change
  const [, tickPrefs] = useState(0);
  useEffect(() => subscribeSections(() => tickPrefs((n) => n + 1)), []);

  // Section number assignment — sections that get hidden don't get a number.
  let num = 0;
  const N = () => String(++num).padStart(2, '0');

  return (
    <PullToRefresh brand={brand} onRefresh={refresh}>
      <div style={{ color: brand.text, paddingBottom: 60 }}>
        {/* HERO — full-bleed clip (or its still) with overlaid type */}
        <div style={{ position: 'relative', height: 560, overflow: 'hidden' }}>
          <HeroMedia state={s.icon === 'partly-cloudy' ? 'cloudy' : s.icon} brand={brand}
                     treatment="punch" motion={heroTreatment !== 'poster'} bucket={bucket} brandKey={brandKey}/>

          <div style={{ position: 'absolute', top: 72, left: 0, right: 0, padding: `0 ${density.pad}px`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button onClick={() => setSheetOpen?.(true)} className="wx-tap" style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999,
              background: 'rgba(0,0,0,.35)', backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,.15)', color: '#fff', cursor: 'pointer',
            }}>
              <Glyph.Pin s={13} c="#fff"/>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{location.label}</span>
              <Glyph.Chevron s={11} c="rgba(255,255,255,.7)"/>
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, background: 'rgba(0,0,0,.35)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,.15)' }}>
              {brand.icon === 'paw' ? <Glyph.Paw s={14} c={brand.accent}/> : <Glyph.FCC s={13} c={brand.accent}/>}
              <span style={{ fontSize: 11, color: '#fff', fontFamily: brand.eyebrowFont, fontWeight: 600 }}>{brand.wordmark}</span>
            </div>
          </div>

          <div style={{ position: 'absolute', top: 126, left: 0, right: 0, padding: `0 ${density.pad + 4}px`, color: '#fff' }}>
            <div style={{ fontSize: 11, fontFamily: brand.eyebrowFont, letterSpacing: '.12em', textTransform: 'uppercase', color: brand.accent, fontWeight: 700, textShadow: '0 2px 10px rgba(0,0,0,.5)' }}>The forecast · today</div>
            <div style={{
              display: 'inline-block', marginTop: 8, maxWidth: 290,
              padding: '8px 12px', borderRadius: 10,
              background: 'rgba(0,0,0,.42)',
              backdropFilter: 'blur(10px) saturate(140%)',
              WebkitBackdropFilter: 'blur(10px) saturate(140%)',
              border: '1px solid rgba(255,255,255,.08)',
              fontSize: 15, color: '#fff', lineHeight: 1.45, fontWeight: 600,
              textShadow: '0 1px 6px rgba(0,0,0,.4)',
            }}>
              {narrative}
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 24, left: 0, right: 0, padding: `0 ${density.pad}px` }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 120, fontWeight: 200, color: '#fff', lineHeight: .85, letterSpacing: '-.05em', fontVariantNumeric: 'tabular-nums', textShadow: '0 6px 30px rgba(0,0,0,.4)' }}>
                  {s.temp}<span style={{ fontSize: '.3em', fontWeight: 400, color: 'rgba(255,255,255,.7)', marginLeft: 4 }}>°{getRawState().unit}</span>
                </div>
                <div style={{ fontSize: 18, color: '#fff', marginTop: 6, fontWeight: 500 }}>{s.cond}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,.75)', marginTop: 2 }}>Feels like {s.feels}° · H {s.hi}° L {s.lo}°</div>
              </div>
              <WxIcon kind={s.icon} size={64}/>
            </div>
          </div>
        </div>

        {showAlert && <div style={{ marginTop: density.gap }}><AlertBanner alert={alert} brand={brand} density={density}/></div>}

        {/* Sun countdown + pressure trend strip */}
        {(sunCount || pressureTrend) && (
          <div style={{ display: 'grid', gridTemplateColumns: sunCount && pressureTrend ? '1fr 1fr' : '1fr', gap: 8, padding: `${density.gap}px ${density.pad}px 0` }}>
            {sunCount && (
              <div style={{ padding: '12px 14px', background: brand.panel, border: `1px solid ${brand.line}`, borderRadius: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                <Glyph.Sun s={18} c={brand.accent}/>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: brand.text, fontWeight: 600 }}>{sunCount.label}</div>
                  <div style={{ fontSize: 11, color: brand.textDim, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sunCount.sub}</div>
                </div>
              </div>
            )}
            {pressureTrend && (
              <div style={{ padding: '12px 14px', background: brand.panel, border: `1px solid ${brand.line}`, borderRadius: 14 }}>
                <div style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em' }}>Pressure</div>
                <div style={{ fontSize: 14, color: brand.text, fontWeight: 600, marginTop: 2 }}>{pressureTrend.label}</div>
                <div style={{ fontSize: 11, color: brand.textDim }}>{pressureTrend.dir}</div>
              </div>
            )}
          </div>
        )}

        {/* 10-day */}
        <Section brand={brand} num={N()} eyebrow="10 days out" title="The long view"/>
        <div style={{ marginTop: density.gap }}>
          {tenDay.map((row, i) => <DayRow key={i} row={row} idx={i} max={max} min={min} brand={brand} density={density} expanded={expanded === i} onToggle={() => setExpanded(expanded === i ? null : i)}/>)}
        </div>

        {/* Hourly + sparklines */}
        <Section brand={brand} num={N()} eyebrow="Through the next 12 hours" title="Hour by hour"/>
        {isSectionEnabled('sparklines') && <HourlySparklines hourly={hourly} brand={brand} density={density}/>}
        <div style={{ display: 'flex', overflowX: 'auto', padding: `${density.gap}px 0 ${density.pad}px`, marginTop: 4 }} className="wx-hide-scroll">
          {hourly.map((hr, i) => <HourCell key={i} hr={hr} brand={brand} density={density} peak={i === 0}/>)}
        </div>

        {/* What to wear */}
        {isSectionEnabled('suggestions') && (
          <>
            <Section brand={brand} num={N()} eyebrow="Outside today" title="What to wear"/>
            <ul style={{ listStyle: 'none', padding: `${density.gap}px ${density.pad}px`, margin: 0 }}>
              {suggestions.map((sg, i) => (
                <li key={i} style={{ display: 'flex', gap: 12, padding: '8px 0', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }} aria-hidden="true">{sg.icon}</span>
                  <span style={{ fontSize: 14, color: brand.text, lineHeight: 1.5 }}>{sg.text}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Minutely precipitation (conditional + toggleable) */}
        {minutely && isSectionEnabled('minutely') && (
          <>
            <Section brand={brand} num={N()} eyebrow="Next 90 minutes" title="Rain in the next hour"/>
            <div style={{ padding: `${density.gap}px ${density.pad}px` }}>
              {/* A fully dry run earns no timeline — six empty bars under
                  "No precipitation expected" said nothing (same collapse as
                  the iOS app). */}
              {minutely.totalP > 0.05 && <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 60 }}>
                {minutely.slices.map((sl, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: '100%', height: 40, display: 'flex', alignItems: 'flex-end' }}>
                      <div style={{
                        width: '100%',
                        height: `${Math.max(4, (sl.precip / minutely.maxP) * 40)}px`,
                        background: `linear-gradient(180deg, ${brand.accentDeep}, #60a5fa)`,
                        borderRadius: 4,
                        opacity: sl.pop > 0 ? .85 : .25,
                      }}/>
                    </div>
                    <div style={{ fontSize: 9, color: brand.textDim, fontFamily: brand.eyebrowFont }}>{sl.time}</div>
                  </div>
                ))}
              </div>}
              <div style={{ fontSize: 12, color: brand.textDim, marginTop: 8 }}>
                {minutely.totalP > 0.05 ? `~${minutely.totalP.toFixed(2)} mm total expected.` : 'No precipitation expected.'}
              </div>
            </div>
          </>
        )}

        {/* Radar (Leaflet + RainViewer) */}
        {isSectionEnabled('radar') && (
          <>
            <Section brand={brand} num={N()} eyebrow="RainViewer · past + nowcast" title="Radar"/>
            <div style={{ padding: `${density.gap}px ${density.pad}px` }}>
              <RadarMap brand={brand}/>
            </div>
          </>
        )}

        {/* UV index timeline */}
        {uvTimeline && uvTimeline.max > 0 && isSectionEnabled('uv') && (
          <>
            <Section brand={brand} num={N()} eyebrow={`Peak ${uvTimeline.max}`} title="UV index"/>
            <div style={{ padding: `${density.gap}px ${density.pad}px` }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 70 }}>
                {uvTimeline.points.map((p, i) => {
                  // NOTE: do not name this `h` — it would shadow the Preact JSX factory
                  // (jsxFactory: 'h', injected via src/preact-h-inject.js) within this
                  // callback scope, making every <div> here compile to a call on the
                  // number → "h is not a function". See build_mobile.js.
                  const barH = Math.max(2, (p.uv / Math.max(1, uvTimeline.max)) * 60);
                  const color = p.uv >= 11 ? '#9333ea' : p.uv >= 8 ? '#dc2626' : p.uv >= 6 ? '#f97316' : p.uv >= 3 ? '#facc15' : '#22c55e';
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{ fontSize: 9, color: brand.textDim, fontFamily: brand.eyebrowFont }}>{p.uv}</div>
                      <div style={{ width: '100%', height: `${barH}px`, background: color, borderRadius: 3, opacity: .85 }}/>
                      <div style={{ fontSize: 8, color: brand.textDim, fontFamily: brand.eyebrowFont }}>{p.hour}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: brand.textDim, marginTop: 8 }}>
                Green &lt;3 · Yellow 3–5 · Orange 6–7 · Red 8–10 · Purple 11+
              </div>
            </div>
          </>
        )}

        {/* Wind detail */}
        {windDetail && isSectionEnabled('wind') && (
          <>
            <Section brand={brand} num={N()} eyebrow={`${windDetail.speed} mph from the ${windDetail.dir}`} title="Wind"/>
            <div style={{ padding: `${density.gap}px ${density.pad}px`, display: 'flex', alignItems: 'center', gap: 18 }}>
              <WindRoseSvg brand={brand} deg={windDetail.deg} speed={windDetail.speed}/>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: 36, fontWeight: 300, color: brand.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{windDetail.speed}</span>
                  <span style={{ fontSize: 13, color: brand.textDim }}>mph</span>
                </div>
                <div style={{ fontSize: 13, color: brand.text, marginTop: 4 }}>From the <b>{windDetail.dir}</b></div>
                {windDetail.gusts != null && (
                  <div style={{ fontSize: 12, color: brand.textDim, marginTop: 2 }}>Gusts to {windDetail.gusts} mph</div>
                )}
              </div>
            </div>
          </>
        )}

        {/* In numbers (always shown) */}
        <Section brand={brand} num={N()} eyebrow="Long-press for why" title="In numbers"/>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, padding: `${density.gap}px ${density.pad}px` }}>
          {metrics.map((m) => <MetricChip key={m.k} m={m} brand={brand} density={density} onLongPress={() => tipFn?.({ tip: m.why, anchor: m.k })}/>)}
        </div>

        {/* Almanac + historical */}
        {almanac.loaded && isSectionEnabled('almanac') && (
          <>
            <Section brand={brand} num={N()} eyebrow="30-year archive" title="Almanac"/>
            <div style={{ padding: `0 ${density.pad}px`, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {[
                { k: 'Avg high',  v: (almanac.avgHi ?? '—') + '°' },
                { k: 'Avg low',   v: (almanac.avgLo ?? '—') + '°' },
                { k: 'Record hi', v: (almanac.recordHi ?? '—') + '°', sub: almanac.recordHiYear ?? '' },
                { k: 'Record lo', v: (almanac.recordLo ?? '—') + '°', sub: almanac.recordLoYear ?? '' },
              ].map((a) => (
                <div key={a.k} style={{ background: brand.panel, border: `1px solid ${brand.line}`, borderRadius: 12, padding: 12 }}>
                  <div style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em' }}>{a.k}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: brand.text, fontVariantNumeric: 'tabular-nums' }}>{a.v}</div>
                  {a.sub !== '' && a.sub != null && <div style={{ fontSize: 11, color: brand.textDim }}>{a.sub}</div>}
                </div>
              ))}
            </div>
            {histDelta && (
              <div style={{ padding: `${density.gap}px ${density.pad}px`, fontSize: 13, color: brand.text }}>
                Today's high is <b>{histDelta.todayHi}°</b> — {histDelta.deltaHi > 0 ? `${histDelta.deltaHi}° warmer` : histDelta.deltaHi < 0 ? `${Math.abs(histDelta.deltaHi)}° cooler` : 'right on'} than the {almanac.recordHiYear ? '30-year' : ''} average of {histDelta.avgHi}°.
              </div>
            )}
          </>
        )}

        {/* Pollen (Europe-only) */}
        {pollen && isSectionEnabled('pollen') && (
          <>
            <Section brand={brand} num={N()} eyebrow="Hourly max today" title="Pollen"/>
            <ul style={{ listStyle: 'none', padding: `${density.gap}px ${density.pad}px`, margin: 0 }}>
              {pollen.map((p) => (
                <li key={p.kind} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 50px', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${brand.line}` }}>
                  <span style={{ fontSize: 13, color: brand.text }}>{p.kind}</span>
                  <div style={{ height: 8, background: 'rgba(255,255,255,.06)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${p.pct}%`, background: p.tier === 'High' ? '#dc2626' : p.tier === 'Moderate' ? '#f97316' : '#22c55e' }}/>
                  </div>
                  <span style={{ fontSize: 11, color: brand.textDim, textAlign: 'right', fontFamily: brand.eyebrowFont }}>{p.tier}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Moon */}
        {isSectionEnabled('moon') && (
          <>
            <Section brand={brand} num={N()} eyebrow="Next 5 nights" title="Moon"/>
            <div style={{ display: 'flex', gap: 6, padding: `${density.gap}px ${density.pad}px`, overflowX: 'auto' }} className="wx-hide-scroll">
              {moonRow.map((m, i) => (
                <div key={i} style={{
                  flex: 1, minWidth: 80, padding: '10px 8px', background: brand.panel, border: `1px solid ${brand.line}`,
                  borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                }}>
                  <div style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase' }}>{m.day}</div>
                  <div style={{ fontSize: 28, lineHeight: 1 }} aria-hidden="true">{m.emoji}</div>
                  <div style={{ fontSize: 12, color: brand.text, fontVariantNumeric: 'tabular-nums' }}>{m.illum}%</div>
                  <div style={{ fontSize: 9, color: brand.textDim, textAlign: 'center', lineHeight: 1.2 }}>{m.phase.split(' ').pop()}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* POI nearby */}
        {isSectionEnabled('poi') && (
          <>
            <Section brand={brand} num={N()} eyebrow={brand.icon === 'paw' ? 'Dog parks · parks · peaks · views' : 'Parks · dog parks · peaks · views'} title="Around you"/>
            <div style={{ padding: `${density.gap}px ${density.pad}px` }}>
              <PoiNearby brand={brand}/>
            </div>
          </>
        )}

        {/* Field notes */}
        {isSectionEnabled('fieldnotes') && (
          <>
            <Section brand={brand} num={N()} eyebrow="From the archive" title="Field notes"/>
            <ul style={{ listStyle: 'none', padding: `${density.gap}px ${density.pad}px`, margin: 0 }}>
              {fieldNotes.map((n, i) => (
                <li key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `1px dashed ${brand.line}` }}>
                  <span style={{ fontSize: 11, color: brand.accent, fontFamily: brand.eyebrowFont, flexShrink: 0, marginTop: 3 }}>0{i+1}</span>
                  <span style={{ fontSize: 14, color: brand.text, lineHeight: 1.5 }}>{n}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Quote */}
        {isSectionEnabled('quote') && (
          <div style={{ padding: `${density.gap}px ${density.pad}px ${density.pad + 12}px` }}>
            <blockquote style={{ margin: 0, padding: '16px 18px', background: brand.panel, borderRadius: 14, borderLeft: `3px solid ${brand.accent}` }}>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, fontStyle: 'italic' }}>"{quote.text}"</p>
              <footer style={{ marginTop: 8, fontSize: 11, color: brand.textDim, fontFamily: brand.eyebrowFont }}>— {quote.author} · <cite style={{ fontStyle: 'normal' }}>{quote.source}</cite></footer>
            </blockquote>
          </div>
        )}

        <AuthFooter brand={brand} density={density}/>

        <div style={{ padding: `0 ${density.pad}px ${density.pad}px`, fontSize: 11, color: brand.textDim, textAlign: 'center', lineHeight: 1.6 }}>
          {updated}<br/>
          Data: Open-Meteo · Alerts: NWS · Radar: RainViewer<br/>
          No ads, no trackers.
        </div>
      </div>
    </PullToRefresh>
  );
}

// Small inline wind rose — compass + speed arrow + cardinal markers.
function WindRoseSvg({ brand, deg, speed }) {
  const size = 84;
  const cx = size / 2, cy = size / 2, r = size / 2 - 6;
  // Wind direction: meteorological "from" angle. Arrow points OUT (downwind).
  const rad = ((deg + 180) % 360 - 90) * Math.PI / 180;
  const ax = cx + Math.cos(rad) * (r - 4);
  const ay = cy + Math.sin(rad) * (r - 4);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label={`Wind ${speed} mph from ${deg}°`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={brand.line} strokeWidth="1"/>
      <circle cx={cx} cy={cy} r={r - 8} fill="none" stroke={brand.line} strokeWidth="1" strokeDasharray="2 3"/>
      {['N','E','S','W'].map((c, i) => {
        const a = (i * 90 - 90) * Math.PI / 180;
        return <text key={c} x={cx + Math.cos(a) * (r + 1)} y={cy + Math.sin(a) * (r + 1) + 3} textAnchor="middle" fontSize="9" fill={brand.textDim} fontFamily={brand.eyebrowFont}>{c}</text>;
      })}
      <line x1={cx} y1={cy} x2={ax} y2={ay} stroke={brand.accent} strokeWidth="2" strokeLinecap="round"/>
      <polygon points={`${ax},${ay} ${ax - Math.cos(rad - 0.4) * 6},${ay - Math.sin(rad - 0.4) * 6} ${ax - Math.cos(rad + 0.4) * 6},${ay - Math.sin(rad + 0.4) * 6}`} fill={brand.accent}/>
      <circle cx={cx} cy={cy} r="2.5" fill={brand.accent}/>
    </svg>
  );
}
